'use client';

import { type IDefaultFormBuilderProps } from '@/components/Form/Fields/types';
import Label from '@/components/Label/Label';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import { MarketingOptInInfoIconModal } from '@/components/marketingOptInInfoIconModal/MarketingOptInInfoIconModal';
import { RichText } from '@/components/richText/RichText';
import { TextField } from '@/components/textField';
import { hasContent } from '@/helper/RichText';
import { useUserStore } from '@/providers/UserStoreProvider';
import type { FormTextStoryblok } from '@/types/component-types-sb';
import { type FieldProps, useFormikContext } from 'formik';
import { usePathname } from 'next/navigation';
import { type FC, useEffect, useState } from 'react';

interface IInputField extends Omit<FormTextStoryblok, 'id'>, FieldProps, IDefaultFormBuilderProps {}

const InputField: FC<IInputField> = ({
  field,
  id,
  label,
  placeholder,
  infoText,
  errorMessage,
  isInvalid,
  required,
  disabled,
  type,
  readOnly,
}) => {
  const formik = useFormikContext<any>();
  const path = usePathname();
  const pathSplitted = path.split('/').filter(Boolean);
  const { loggedIn, email } = useUserStore((state) => ({
    loggedIn: state.loggedIn,
    email: state.email,
  }));

  // If the user is logged in and a marketing_opt_in field is present in the form, we have to check if the input field
  // is the email field which is used for sending the opt in. Given that case we have to prefill the fieldw ith the email address from
  // gigya and disable the input field. We also display a information icon (but only if we are outside of the vorteilswelt)
  // see https://myswd.atlassian.net/browse/NEXT-986 for more details
  const isInVtw = pathSplitted.length > 0 ? pathSplitted[0] === 'vorteilswelt' : false;

  // State to manage the final disabled status, considering Gigya session asynchronously
  const [isLoggedInAndEmailField, setLoggedInAndEmailField] = useState(false);

  useEffect(() => {
    const shouldCheckLoginInfo = id === 'email' && 'marketing_opt_in' in formik.values;
    if (shouldCheckLoginInfo) {
      setLoggedInAndEmailField(loggedIn && !!email);
    } else {
      setLoggedInAndEmailField(false);
    }
  }, [loggedIn, email, id, formik.values]);

  const infoAdornment =
    isLoggedInAndEmailField && !isInVtw ? (
      <MarketingOptInInfoIconModal />
    ) : hasContent(infoText) ? (
      <InfoIconModal trackingLabel={label ?? field.name}>
        <RichText text={infoText} />
      </InfoIconModal>
    ) : undefined;

  return (
    <TextField
      {...field}
      required={required}
      id={id}
      type={type}
      disabled={disabled || isLoggedInAndEmailField}
      placeholder={placeholder}
      label={label && <Label text={label} />}
      helperText={isInvalid && errorMessage}
      error={isInvalid}
      fullWidth
      InputProps={{
        endAdornment: infoAdornment,
        readOnly,
        inputProps: {
          tabIndex: readOnly ? -1 : 0,
        },
      }}
      inputProps={{ 'data-testid': 'form-builder-input-field' }}
    />
  );
};

export default InputField;
